import React from 'react';

import type { LookoTutorialModalProps } from '@looko-design-system';
import { LookoModal } from '@looko-design-system';
import { atom, useAtom, useSetAtom } from 'jotai';

import type { PartialSome } from '@looko-corp/acloset-react-kit/src/utils';

export type LookoTutorialModalState = Omit<LookoTutorialModalProps, 'open'>;

const eventAlertDialogDefaultValue: LookoTutorialModalState = {
  contents: [],
  confirmButtonText: '확인',
  confirmButtonColor: undefined,
  cancelButtonText: '취소',
  onClose: () => {},
  onConfirm: () => true,
};

type AtomType = LookoTutorialModalState[];

const globalTutorialModalValues = atom<AtomType>([]);

/**
 * 글로벌 Tutorial Modal
 *
 * @see https://www.figma.com/file/PFrAvDaThtf8qMSRc9pNaV/LDS(Looko-Design-System)?node-id=4323%3A9439&mode=dev
 */
export function GlobalTutorialModal() {
  const [state, update] = useAtom(globalTutorialModalValues);

  const currentDialog = state[0];

  const handleClose = React.useCallback(() => {
    update(value => {
      const [_removed, ...rest] = value;
      return rest;
    });

    currentDialog?.onClose?.();
  }, [update, currentDialog]);

  if (state.length === 0) {
    return;
  }

  return <LookoModal.Tutorial {...currentDialog} open={currentDialog != null} onClose={handleClose} />;
}

/**
 * 글로벌 Tutorial Modal을 부르는 핸들러를 가져옵니다.
 *
 * @returns openModal 모달을 엽니다.
 * @returns deleteModal 삭제 버튼이 있는 모달을 엽니다.
 */
export function useGlobalTutorialModal() {
  const updateValue = useSetAtom(globalTutorialModalValues);

  const resetModal = () => updateValue([]);

  const openModal = React.useCallback(
    (values: PartialSome<LookoTutorialModalState, 'onConfirm'>) => {
      updateValue(prev => {
        return [...prev, { ...eventAlertDialogDefaultValue, ...values }];
      });
    },
    [updateValue],
  );

  const deleteModal = React.useCallback(
    (values: PartialSome<LookoTutorialModalState, 'onConfirm'>) => {
      updateValue(prev => {
        return [
          ...prev,
          {
            ...eventAlertDialogDefaultValue,
            confirmButtonText: '삭제',
            confirmButtonColor: 'error',
            ...values,
          },
        ];
      });
    },
    [updateValue],
  );

  return { openModal, deleteModal, resetModal };
}
